import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import cx from 'classnames';
import { Row, Col, Checkbox, Spin, Card, Alert, Tabs } from 'antd';
import * as actions from '@actions';
import styled from '@emotion/styled';
import { useSessionAppointment } from '@hooks/useSessionAppointment';

const StyledGpHealthRecordStyles = styled.div`
  font-size: 0.9em;

  .patient-consent {
    width: 100%;
    text-align: center;
    margin-bottom: 10px;
    font-style: italic;
  }

  .patient-card {
    margin-bottom: 20px;
    font-weight: 600;
    border: 1px solid var(--productPrimary);

    small {
      font-weight: normal;
    }

    .ant-card-head {
      color: var(--productPrimary);
      font-weight: 600;

      .ant-card-extra {
        color: var(--productPrimary);
        font-weight: 600;

        small {
          margin-left: 20px;
        }
      }
    }

    .ant-card-grid {
      box-shadow: none;
      padding: 10px;
    }
  }

  .tab {
    box-shadow: none;
    border-radius: 0px;
  }

  .ant-tabs-left > .ant-tabs-nav .ant-tabs-tab {
    padding: 8px 20px 0px 0px;
    font-style: italic;
    font-size: 0.9em;
    font-weight: 600;
  }

  .tab-content h1 {
    display: none;
  }

  .tab-content h2 {
    font-size: 18px;
    color: black;
    font-weight: 600;
  }

  .tab-content table {
    width: 100%;
    text-align: left;
    border-radius: 2px 2px 0 0;
    border-collapse: separate;
    border-spacing: 0;
    border-color: grey;
    margin-bottom: 30px;
  }

  .tab-content tbody > tr > td,
  .tab-content thead > tr > th,
  .tab-content tfoot > tr > td,
  .tab-content tfoot > tr > th {
    padding: 12px 8px;
  }

  .tab-content thead > tr > th {
    color: rgba(0, 0, 0, 0.85);
    font-weight: 600;
    border-bottom: 1px solid #f0f0f0;
  }
  .tab-content tbody > tr > td {
    border-bottom: 1px solid #f0f0f0;
    transition: background 0.3s;
  }
  .gptransfer-banner {
    color: red;
  }
`;

const StyledSpin = styled(Spin)`
  text-align: center;
  margin-top: 10em;
  width: 100%;
`;

const GpConnectTabEnum = {
  SUM: 'Summary',
  ENC: 'Encounters',
  CLI: 'Clinical Items',
  PRB: 'Problems and Issues',
  ALL: 'Allergies and Adverse Reactions',
  MED: 'Medications',
  REF: 'Referrals',
  OBS: 'Observations',
  IMM: 'Immunisation',
  ADM: 'Administrative Items'
};

const GPHealthRecord = ({ className = '' }: { className?: string }) => {
  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const classNames = cx('clinician-gprecord', className);
  const { appointment } = useSessionAppointment();
  const [gpHealthRecordConsent, setGpHealthRecordConsent] = useState(false);
  const [gpRecordName, setGpRecordName] = useState(false);
  const [gpRecordGender, setGpRecordGender] = useState(false);
  const [gpRecordDob, setGpRecordDob] = useState(false);
  const [gpRecordAge, setGpRecordAge] = useState<number>();
  const [gpRecordNhsNo, setGpRecordNhsNo] = useState(false);
  const [gpRecordAddress, setGpRecordAddress] = useState(false);
  const [gpRecordTel, setGpRecordTel] = useState(false);
  const [gpRecordGp, setGpRecordGp] = useState(false);
  const [gpRecordPas, setGpRecordPas] = useState(false);

  const [gpRecordSummary, setGpRecordSummary] = useState(false);
  const [gpRecordEncounters, setGpRecordEncounters] = useState(false);
  const [gpRecordClinicianItems, setGpRecordClinicianItems] = useState(false);
  const [gpRecordProblemsAndIssues, setGpRecordProblemsAndIssues] = useState(false);
  const [gpRecordAllergiesAndAdverseReactions, setGpRecordAllergiesAndAdverseReactions] = useState(false);
  const [gpRecordMedications, setGpRecordMedications] = useState(false);
  const [gpRecordReferrals, setGpRecordReferrals] = useState(false);
  const [gpRecordObservations, setGpRecordObservations] = useState(false);
  const [gpRecordImmunisation, setGpRecordImmunisations] = useState(false);
  const [gpRecordAdministrativeItems, setGpRecordAdministrativeItems] = useState(false);

  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [superseded, setSuperseded] = useState(false);

  const getAge = (dateString: string) => {
    const today = new Date();
    const birthDate = new Date(dateString);
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age -= 1;
    }
    return age;
  };

  const getRecordByPage = useCallback(async () => {
    setErrorCode(null);
    setSuperseded(false);
    await dispatch(actions.getGPRecords(appointment.appointment_id, 'SUM'), false)
      .then(async (res) => {
        if (res?.html) {
          setGpRecordSummary(res.html);
          setGpRecordName(res.patientSummaryInfo.name);
          setGpRecordGender(res.patientSummaryInfo.gender);
          setGpRecordDob(res.patientSummaryInfo.birthdate);
          setGpRecordAge(getAge(res.patientSummaryInfo.birthdate));
          setGpRecordNhsNo(res.patientSummaryInfo.nhsno);
          setGpRecordAddress(res.patientSummaryInfo.address);
          setGpRecordTel(res.patientSummaryInfo.tel);
          setGpRecordGp(res.patientSummaryInfo.gp);
          setGpRecordPas(res.patientSummaryInfo.pas);
        } else {
          const error_res = await res.json();
          if (error_res?.isSuperseded) {
            setSuperseded(error_res?.isSuperseded);
          }
          if (error_res?.display === 'INVALID_RESOURCE_ID') {
            setErrorCode('INVALID_RESOURCE_ID');
          } else if (error_res?.error === 'DECEASED') {
            setErrorCode('DECEASED');
          } else {
            setErrorCode(error_res?.error || 'No error message');
          }
        }
      })
      .catch((e) => {
        setErrorCode(e);
      });
  }, [dispatch, appointment?.appointment_id]);

  useEffect(() => {
    if (gpHealthRecordConsent && !gpRecordSummary) {
      getRecordByPage();
    }
  }, [gpHealthRecordConsent, gpRecordSummary, getRecordByPage]);

  const SumContent = (
    <div key="SUM">
      {gpRecordSummary ? (
        <div className="tab-content" dangerouslySetInnerHTML={{ __html: gpRecordSummary }} />
      ) : (
        <StyledSpin size="large" />
      )}
    </div>
  );

  const EncContent = (
    <div key="ENC">
      {gpRecordEncounters ? (
        <div className="tab-content" dangerouslySetInnerHTML={{ __html: gpRecordEncounters }} />
      ) : (
        <StyledSpin size="large" />
      )}
    </div>
  );

  const CliContent = (
    <div key="CLI">
      {gpRecordClinicianItems ? (
        <div className="tab-content" dangerouslySetInnerHTML={{ __html: gpRecordClinicianItems }} />
      ) : (
        <StyledSpin size="large" />
      )}
    </div>
  );
  const PrbContent = (
    <div key="PRB">
      {gpRecordProblemsAndIssues ? (
        <div className="tab-content" dangerouslySetInnerHTML={{ __html: gpRecordProblemsAndIssues }} />
      ) : (
        <StyledSpin size="large" />
      )}
    </div>
  );
  const AllContent = (
    <div key="ALL">
      {gpRecordAllergiesAndAdverseReactions ? (
        <div className="tab-content" dangerouslySetInnerHTML={{ __html: gpRecordAllergiesAndAdverseReactions }} />
      ) : (
        <StyledSpin size="large" />
      )}
    </div>
  );
  const MedContent = (
    <div key="MED">
      {gpRecordMedications ? (
        <div className="tab-content" dangerouslySetInnerHTML={{ __html: gpRecordMedications }} />
      ) : (
        <StyledSpin size="large" />
      )}
    </div>
  );
  const RefContent = (
    <div key="REF">
      {gpRecordReferrals ? (
        <div className="tab-content" dangerouslySetInnerHTML={{ __html: gpRecordReferrals }} />
      ) : (
        <StyledSpin size="large" />
      )}
    </div>
  );
  const ObsContent = (
    <div key="OBS">
      {gpRecordObservations ? (
        <div className="tab-content" dangerouslySetInnerHTML={{ __html: gpRecordObservations }} />
      ) : (
        <StyledSpin size="large" />
      )}
    </div>
  );
  const ImmContent = (
    <div key="IMM">
      {gpRecordImmunisation ? (
        <div className="tab-content" dangerouslySetInnerHTML={{ __html: gpRecordImmunisation }} />
      ) : (
        <StyledSpin size="large" />
      )}
    </div>
  );
  const AdmContent = (
    <div key="ADM">
      {gpRecordAdministrativeItems ? (
        <div className="tab-content" dangerouslySetInnerHTML={{ __html: gpRecordAdministrativeItems }} />
      ) : (
        <StyledSpin size="large" />
      )}
    </div>
  );

  const tabItems = [
    {
      key: 'SUM',
      label: GpConnectTabEnum.SUM,
      children: SumContent
    },
    {
      key: 'ENC',
      label: GpConnectTabEnum.ENC,
      children: EncContent
    },
    {
      key: 'CLI',
      label: GpConnectTabEnum.CLI,
      children: CliContent
    },
    {
      key: 'PRB',
      label: GpConnectTabEnum.PRB,
      children: PrbContent
    },
    {
      key: 'ALL',
      label: GpConnectTabEnum.ALL,
      children: AllContent
    },
    {
      key: 'MED',
      label: GpConnectTabEnum.MED,
      children: MedContent
    },
    {
      key: 'REF',
      label: GpConnectTabEnum.REF,
      children: RefContent
    },
    {
      key: 'OBS',
      label: GpConnectTabEnum.OBS,
      children: ObsContent
    },
    {
      key: 'IMM',
      label: GpConnectTabEnum.IMM,
      children: ImmContent
    },
    {
      key: 'ADM',
      label: GpConnectTabEnum.ADM,
      children: AdmContent
    }
  ];

  return (
    <StyledGpHealthRecordStyles className={classNames}>
      <Checkbox
        className="patient-consent"
        checked={gpHealthRecordConsent}
        onChange={(e) => setGpHealthRecordConsent(e.target.checked)}
        data-testid="consent-checkbox"
      >
        {t('The patient has given their consent for me to access their GP Health Record')}
      </Checkbox>

      {gpHealthRecordConsent && (
        <Row>
          <Col span={24}>
            {superseded && (
              <Alert
                message={t('This patients NHS number has been superseded and replaced. Please inform the pat')}
                description={superseded}
                type="warning"
                css={{ marginTop: 20 }}
                showIcon
              />
            )}
            {errorCode ? (
              <Alert
                message={
                  errorCode === 'INVALID_RESOURCE_ID'
                    ? 'This patient&apos;s NHS number is no longer valid'
                    : t('Unable to get patient records')
                }
                description={
                  {
                    INVALID_RESOURCE_ID:
                      'The record is being referred to the Local Back Office for processing. ' +
                      'All demographic and clinical information for this patient should be ' +
                      'regarded with caution until the processing is complete.',
                    DECEASED: 'Records show the patient with this NHS number is deceased'
                  }[errorCode] || 'Please ask the patient for their medical history'
                }
                type={errorCode === 'INVALID_RESOURCE_ID' || errorCode === 'DECEASED' ? 'error' : 'warning'}
                css={{ marginTop: 20 }}
                showIcon
              />
            ) : (
              <>
                <Card
                  className="patient-card"
                  size="small"
                  title={gpRecordName}
                  extra={
                    <>
                      <small>{t('Gender:')}</small> {gpRecordGender}
                      <small>{t('DOB:')}</small> {gpRecordDob}
                      <small>{t('Age:')}</small> {gpRecordAge}
                      <small>{t('NHS No.:')}</small> {gpRecordNhsNo}
                    </>
                  }
                >
                  <Card.Grid hoverable={false} style={{ width: '30%' }}>
                    <small>{t('Address:')}</small> {gpRecordAddress}
                  </Card.Grid>
                  <Card.Grid hoverable={false} style={{ width: '25%' }}>
                    <small>{t('Phone:')}</small> {gpRecordTel}
                  </Card.Grid>
                  <Card.Grid hoverable={false} style={{ width: '25%' }}>
                    <small>{t('GP Details:')}</small> {gpRecordGp}
                  </Card.Grid>
                  <Card.Grid hoverable={false} style={{ width: '20%' }}>
                    <small>{t('PAS No.:')}</small> {gpRecordPas}
                  </Card.Grid>
                </Card>
                <Tabs
                  tabPosition="left"
                  onChange={async (active) => {
                    switch (active) {
                      case 'ENC':
                        if (!gpRecordEncounters) {
                          await dispatch(actions.getGPRecords(appointment.appointment_id, 'ENC'), false)
                            .then((encounters) => {
                              setGpRecordEncounters(encounters.html);
                            })
                            .catch(async (res) => {
                              const error_res = await res.json();
                              setErrorCode(error_res?.error || 'No error message');
                            });
                        }
                        break;
                      case 'CLI':
                        if (!gpRecordClinicianItems) {
                          await dispatch(actions.getGPRecords(appointment.appointment_id, 'CLI'), false)
                            .then((clinicalItems) => {
                              setGpRecordClinicianItems(clinicalItems.html);
                            })
                            .catch(async (res) => {
                              const error_res = await res.json();
                              setErrorCode(error_res?.error || 'No error message');
                            });
                        }
                        break;
                      case 'PRB':
                        if (!gpRecordProblemsAndIssues) {
                          await dispatch(actions.getGPRecords(appointment.appointment_id, 'PRB'), false)
                            .then((problemsAndIssues) => {
                              setGpRecordProblemsAndIssues(problemsAndIssues.html);
                            })
                            .catch(async (res) => {
                              const error_res = await res.json();
                              setErrorCode(error_res?.error || 'No error message');
                            });
                        }
                        break;
                      case 'ALL':
                        if (!gpRecordAllergiesAndAdverseReactions) {
                          await dispatch(actions.getGPRecords(appointment.appointment_id, 'ALL'), false)
                            .then((allergiesAndAdverseReactions) => {
                              setGpRecordAllergiesAndAdverseReactions(allergiesAndAdverseReactions.html);
                            })
                            .catch(async (res) => {
                              const error_res = await res.json();
                              setErrorCode(error_res?.error || 'No error message');
                            });
                        }
                        break;
                      case 'MED':
                        if (!gpRecordMedications) {
                          await dispatch(actions.getGPRecords(appointment.appointment_id, 'MED'), false)
                            .then((medications) => {
                              setGpRecordMedications(medications.html);
                            })
                            .catch(async (res) => {
                              const error_res = await res.json();
                              setErrorCode(error_res?.error || 'No error message');
                            });
                        }
                        break;
                      case 'REF':
                        if (!gpRecordReferrals) {
                          await dispatch(actions.getGPRecords(appointment.appointment_id, 'REF'), false)
                            .then((referrals) => {
                              setGpRecordReferrals(referrals.html);
                            })
                            .catch(async (res) => {
                              const error_res = await res.json();
                              setErrorCode(error_res?.error || 'No error message');
                            });
                        }
                        break;
                      case 'OBS':
                        if (!gpRecordObservations) {
                          await dispatch(actions.getGPRecords(appointment.appointment_id, 'OBS'), false)
                            .then((observations) => {
                              setGpRecordObservations(observations.html);
                            })
                            .catch(async (res) => {
                              const error_res = await res.json();
                              setErrorCode(error_res?.error || 'No error message');
                            });
                        }
                        break;
                      case 'IMM':
                        if (!gpRecordImmunisation) {
                          await dispatch(actions.getGPRecords(appointment.appointment_id, 'IMM'), false)
                            .then((immunisation) => {
                              setGpRecordImmunisations(immunisation.html);
                            })
                            .catch(async (res) => {
                              const error_res = await res.json();
                              setErrorCode(error_res?.error || 'No error message');
                            });
                        }
                        break;
                      case 'ADM':
                        if (!gpRecordAdministrativeItems) {
                          await dispatch(actions.getGPRecords(appointment.appointment_id, 'ADM'), false)
                            .then((administrativeItems) => {
                              setGpRecordAdministrativeItems(administrativeItems.html);
                            })
                            .catch(async (res) => {
                              const error_res = await res.json();
                              setErrorCode(error_res?.error || 'No error message');
                            });
                        }
                        break;
                      default: // Do nothing
                    }
                  }}
                  items={tabItems}
                />
              </>
            )}
          </Col>
        </Row>
      )}
    </StyledGpHealthRecordStyles>
  );
};

export default GPHealthRecord;
